import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { subscribeToEvent, subscribeToDiscipline, subscribeToEventBouts } from "../firebase/services";
import PoolStandings from "../components/PoolStandings";
import { BracketTree } from "../components/EliminationBracket";
import { DEFAULT_DISCIPLINE_VISIBILITY } from "../utils/scoring";
import logo from "../assets/logo.png";

export default function PublicDiscipline() {
  const { eventId, disciplineId } = useParams();
  const [event, setEvent] = useState(null);
  const [disc, setDisc] = useState(null);
  const [bouts, setBouts] = useState([]);
  const [activeTab, setActiveTab] = useState("fighters");
  const [collapsedPools, setCollapsedPools] = useState(new Set());

  const togglePool = (name) => {
    setCollapsedPools(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  useEffect(() => {
    const unsub1 = subscribeToEvent(eventId, setEvent);
    const unsub2 = subscribeToDiscipline(eventId, disciplineId, setDisc);
    const unsub3 = subscribeToEventBouts(eventId, (allBouts) => {
      setBouts(allBouts.filter(b => b.disciplineId === disciplineId));
    });
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [eventId, disciplineId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [disciplineId, activeTab]);

  if (!disc || !event) return <div className="flex items-center justify-center min-h-screen text-gray-600">Loading...</div>;

  const rawVis = disc.visibility || {};
  const vis = {
    pools: rawVis.pools ?? DEFAULT_DISCIPLINE_VISIBILITY.pools,
    scores: rawVis.scores ?? DEFAULT_DISCIPLINE_VISIBILITY.scores,
    standings: rawVis.standings ?? DEFAULT_DISCIPLINE_VISIBILITY.standings,
    elimBracket: rawVis.elimBracket ?? DEFAULT_DISCIPLINE_VISIBILITY.elimBracket,
    elimScores: rawVis.elimScores ?? DEFAULT_DISCIPLINE_VISIBILITY.elimScores,
    judgeBreakdown: rawVis.judgeBreakdown ?? DEFAULT_DISCIPLINE_VISIBILITY.judgeBreakdown,
  };
  const fighters = disc.fighters || [];
  const pools = disc.pools || [];
  const elimination = disc.elimination || null;
  const poolBouts = bouts.filter(b => b.eliminationRound == null);
  const elimBouts = bouts.filter(b => b.eliminationRound != null);
  const confirmedPoolBouts = poolBouts.filter(b => b.status === "confirmed");
  const showPoolsTab = vis.pools && (pools.length > 0 || poolBouts.length > 0);
  const showStandingsTab = vis.standings && confirmedPoolBouts.length > 0 && pools.length > 0;
  const showElimTab = vis.elimBracket && elimination && elimBouts.length > 0;

  const TABS = [
    { key: "fighters", label: `FIGHTERS (${fighters.length})` },
    showPoolsTab && { key: "pools", label: `POOLS & BOUTS${pools.length ? ` (${pools.length})` : ""}` },
    showStandingsTab && { key: "standings", label: "STANDINGS" },
    showElimTab && { key: "elimination", label: "ELIMINATION" },
  ].filter(Boolean);
  const currentTab = TABS.some(t => t.key === activeTab) ? activeTab : TABS[0]?.key;

  return (
    <div className="min-h-screen p-5 lg:max-w-5xl lg:mx-auto">
      <Link to={`/public/${eventId}`} className="text-gray-600 text-sm no-underline hover:text-gray-400">← Back to {event.name || "Event"}</Link>

      <div className="text-center my-4">
        <img src={logo} alt="Joy of HEMA" className="w-80 mx-auto" />
        <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-sm font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
      </div>

      <div className="mt-3 mb-1">
        <h1 className="font-display text-xl font-bold">{disc.name}</h1>
      </div>
      <div className="text-gray-600 text-xs mb-4">{event.name}</div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-5 border-b border-dark-500 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} aria-pressed={currentTab === t.key}
            className="px-4 py-2.5 font-display text-xs font-bold tracking-widest whitespace-nowrap border-b-2 bg-transparent cursor-pointer"
            style={{ color: currentTab === t.key ? "#fff" : "#bbb", borderColor: currentTab === t.key ? "#81C784" : "transparent" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Fighters */}
      {currentTab === "fighters" && (
        fighters.length > 0 ? (
          <div className="overflow-x-auto border border-dark-500 rounded-lg">
            <table className="w-full text-sm border-collapse">
              <caption className="sr-only">Fighter check-in status</caption>
              <thead>
                <tr className="bg-dark-700">
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">NAME</th>
                  <th scope="col" className="text-left p-2.5 text-xs font-bold text-gray-500 tracking-wider">AFFILIATION</th>
                  <th scope="col" className="text-center p-2.5 text-xs font-bold text-gray-500 tracking-wider">CHECKED IN</th>
                </tr>
              </thead>
              <tbody>
                {fighters.sort().map(name => {
                  const checkedIn = (disc.fighterCheckins || {})[name] || false;
                  return (
                  <tr key={name} className="border-t border-dark-500">
                    <th scope="row" className="p-2.5 text-left font-normal text-gray-300 whitespace-nowrap">{name}</th>
                    <td className="p-2.5 text-gray-500">{(event?.fighterAffiliations || {})[name] || "—"}</td>
                    <td className="p-2.5 text-center">
                      <span className={checkedIn ? "text-green-400" : "text-gray-600"}>{checkedIn ? "✓" : "—"}</span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-600 text-sm">No fighters listed.</p>
        )
      )}

      {/* Pools & Bouts */}
      {currentTab === "pools" && (
        <div>
          {pools.map((pool, i) => {
            const thisBouts = poolBouts.filter(b => b.poolName === pool.name).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const completed = thisBouts.filter(b => b.status === "confirmed").length;
            const isOpen = !collapsedPools.has(pool.name);
            return (
              <div key={i} className="mb-4">
                <button onClick={() => togglePool(pool.name)} aria-expanded={isOpen}
                  className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-3 mb-2 cursor-pointer">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-display text-sm font-bold text-white">{pool.name}</div>
                    <div className="flex items-center gap-2">
                      {thisBouts.length > 0 && <span className="text-xs text-gray-600">{completed}/{thisBouts.length} done</span>}
                      <span aria-hidden="true" className="text-gray-600 text-xs">{isOpen ? "▲" : "▼"}</span>
                    </div>
                  </div>
                  {pool.location && (
                    <div className="text-xs text-gray-500 mb-2">📍 {pool.location}</div>
                  )}
                  {(pool.ref || pool.judges?.length > 0) && (
                    <div className="text-xs text-gray-400 mb-2 space-y-1">
                      {pool.ref && <div><span className="text-gray-600">Referee: </span>{pool.ref}</div>}
                      {pool.judges?.length > 0 && <div><span className="text-gray-600">Judges: </span>{pool.judges.join(", ")}</div>}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {pool.fighters.map(f => (
                      <span key={f} className="px-2.5 py-1 bg-dark-800 border border-dark-500 rounded-lg text-xs text-gray-300">{f}</span>
                    ))}
                  </div>
                </button>
                <Link to={`/spectate/pool/${eventId}/${disciplineId}/${encodeURIComponent(pool.name)}?view=live`}
                  className="flex items-center justify-center w-full mb-2 py-2.5 rounded-lg border border-green-700 bg-green-900/20 text-green-300 text-xs font-display font-bold tracking-widest no-underline hover:bg-green-900/40 transition-colors">
                  LIVE SCOREBOARD →
                </Link>
                {isOpen && (thisBouts.length > 0
                  ? thisBouts.map(bout => <PublicBoutCard key={bout.id} bout={bout} vis={vis} />)
                  : <p className="text-gray-600 text-xs px-1">No bouts generated yet.</p>)}
              </div>
            );
          })}
          {poolBouts.filter(b => !b.poolName).map(bout => (
            <PublicBoutCard key={bout.id} bout={bout} vis={vis} />
          ))}
        </div>
      )}

      {/* Pool Standings */}
      {currentTab === "standings" && (
        <PoolStandings pools={pools} bouts={poolBouts} showBracketCutoff={false} advancingCount={elimination?.bracketSize || 0} />
      )}

      {/* Elimination Bracket */}
      {currentTab === "elimination" && (
        <BracketTree bracketSize={elimination.bracketSize} bouts={bouts} showScores={vis.elimScores} />
      )}
    </div>
  );
}

function PublicBoutCard({ bout, vis }) {
  const statusLabel = {
    waiting: "Upcoming", scoring: "In Progress", submitted: "In Review", confirmed: "✓ Done",
  }[bout.status] || bout.status;
  const statusColor = {
    waiting: "#888", scoring: "#64B5F6", submitted: "#FFB300", confirmed: "#4CAF50",
  }[bout.status] || "#888";

  return (
    <div className="bg-dark-700 border border-dark-500 rounded-xl p-3 mb-2">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="font-display text-sm font-bold" style={{ color: "#64B5F6" }}>{bout.fighterBlue}</span>
          <span className="text-gray-600 text-xs">vs</span>
          <span className="font-display text-sm font-bold" style={{ color: "#EF9A9A" }}>{bout.fighterRed}</span>
        </div>
        <span className="font-display text-xs font-bold" style={{ color: statusColor }}>{statusLabel}</span>
      </div>

      {vis.scores && bout.confirmedResult && (
        <div className="flex items-center gap-2 text-xs mt-1">
          <span className="font-display font-bold" style={{ color: "#1E88E5" }}>{bout.confirmedResult.scoreB}</span>
          <span className="text-gray-700">–</span>
          <span className="font-display font-bold" style={{ color: "#E53935" }}>{bout.confirmedResult.scoreA}</span>
          {bout.confirmedResult.winner && bout.confirmedResult.winner !== "Draw" && (
            <span className="ml-auto text-xs font-display font-bold" style={{ color: "#4CAF50" }}>Winner: {bout.confirmedResult.winner}</span>
          )}
          {bout.confirmedResult.winner === "Draw" && (
            <span className="ml-auto text-xs font-display font-bold text-gray-500">Draw</span>
          )}
        </div>
      )}

      {vis.judgeBreakdown && bout.status === "confirmed" && bout.judgeSubmissions && (
        <div className="mt-2 pt-2 border-t border-dark-500/30">
          {Object.entries(bout.judgeSubmissions).map(([key, sub]) => {
            if (!sub?.complete) return null;
            return (
              <div key={key} className="py-0.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">{sub.judgeName || key}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="font-display font-bold" style={{ color: "#1E88E5" }}>{sub.totalBlue}</span>
                    <span className="text-gray-700">–</span>
                    <span className="font-display font-bold" style={{ color: "#E53935" }}>{sub.totalRed}</span>
                  </div>
                </div>
                {sub.boutWinnerComment && <div className="text-[11px] text-gray-600 italic mt-0.5">"{sub.boutWinnerComment}"</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
